import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useChat } from "@/contexts/ChatContext";
import { useGetMeetStatusQuery } from "@/services/googleMeet";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { isStrictAdmin } from "@/helpers/UserHelpers";
import { ConversationView } from "@/interfaces/Chat";
export default function ConversationHeader({
  conversation,
  onMembers,
}: {
  conversation: ConversationView;
  onMembers: () => void;
}) {
  const chat = useChat();
  const admin = isStrictAdmin(useSelector(selectCurrentUser));
  const status = useGetMeetStatusQuery(
    { actorId: chat.actorId ?? "" },
    {
      skip: !chat.actorId,
      refetchOnMountOrArgChange: true,
    }
  );
  const entry = chat.meetOperations[conversation.id];
  const operation = entry?.operation;
  const publication = operation?.code === "MEET_PUBLICATION_RETRY_REQUIRED";
  const uncertain = operation?.status === "uncertain";
  const failed = operation?.status === "failed";
  const waiting =
    entry?.busy ||
    (operation?.status === "pending" && !publication && !entry.error);
  const unconfigured =
    status.currentData?.available === false ||
    (status.currentData?.available !== true &&
      [
        "MEET_UNCONFIGURED",
        "MEET_RECONNECT_REQUIRED",
        "MEET_CONNECTION_CHANGED",
      ].includes(operation?.code ?? ""));
  const label = waiting
    ? "Création du Meet…"
    : publication
      ? "Réessayer le partage"
      : uncertain
        ? "Créer un nouveau lien"
        : entry?.error
          ? "Réessayer la demande"
          : failed
            ? "Réessayer la création"
            : "Créer un Meet";
  return (
    <header className="chat-header">
      <Button
        as={Link as any}
        to="/chat"
        variant="light"
        className="chat-back"
        aria-label="Retour aux conversations"
      >
        ←
      </Button>
      <span className="chat-avatar" aria-hidden="true">
        {conversation.kind === "group"
          ? "#"
          : conversation.name.slice(0, 2).toUpperCase()}
      </span>
      <div className="chat-header-title">
        <h2>{conversation.name}</h2>
        <span>
          {conversation.kind === "group"
            ? `${conversation.memberCount} membres`
            : "Conversation privée"}
        </span>
      </div>
      <div className="chat-meet-control">
        <Button
          variant="outline-primary"
          disabled={
            !conversation.canWrite ||
            !!waiting ||
            (!entry && status.isFetching) ||
            (unconfigured && !publication && !entry?.error)
          }
          onClick={() => void chat.createMeet(conversation.id)}
        >
          {label}
        </Button>
      </div>
      {conversation.kind === "group" && (
        <Button variant="outline-secondary" onClick={onMembers}>
          Membres
        </Button>
      )}
      {(unconfigured ||
        entry?.error ||
        publication ||
        uncertain ||
        failed ||
        operation?.status === "completed" ||
        status.isError) && (
        <div
          className="chat-meet-feedback"
          role={entry?.error || failed || uncertain ? "alert" : "status"}
        >
          {unconfigured ? (
            <>
              Google Meet n’est pas connecté.{" "}
              {admin ? (
                <Link to="/admin/google-meet">
                  Ouvrir les réglages Google Meet
                </Link>
              ) : (
                "Contactez un administrateur."
              )}
            </>
          ) : entry?.error ? (
            entry.error
          ) : publication ? (
            "Le lien est créé. Réessayez son partage dans cette conversation."
          ) : uncertain ? (
            "La création n’a pas pu être confirmée. Créer un nouveau lien peut produire une seconde réunion."
          ) : failed ? (
            "La création a échoué. Réessayez lorsque Google Meet est disponible."
          ) : operation?.status === "completed" ? (
            "Lien partagé dans la conversation."
          ) : (
            "Impossible de vérifier la disponibilité de Google Meet."
          )}
        </div>
      )}
    </header>
  );
}
